import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle, Mail } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { useBranding, DEFAULT_LOGO_URL } from '@/contexts/BrandingContext';
import { useLanguage } from '@/i18n/LanguageContext';
import Captcha from '@/components/Captcha';
import LanguageSelector from '@/components/LanguageSelector';
import { analytics, trackSignupSuccess } from '@/lib/analytics';

function CompanyAdminSignup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { branding } = useBranding();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [verificationPending, setVerificationPending] = useState(false);
  const [signupEmail, setSignupEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [logoSrc, setLogoSrc] = useState(branding?.logo_url || DEFAULT_LOGO_URL);
  const captchaRef = useRef(null);
  const formStartedRef = useRef(false);
  
  // Form fields
  const [companyName, setCompanyName] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [country, setCountry] = useState('');
  
  // Referral code from URL — applied silently on submit (no banner UI)
  const referralCode = searchParams.get('ref');
  
  useEffect(() => {
    analytics.signupStart();
  }, []);

  useEffect(() => {
    setLogoSrc(branding?.logo_url || DEFAULT_LOGO_URL);
  }, [branding?.logo_url]);

  const markFormStart = () => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    analytics.formStart('admin_signup');
  };
  
  const handleCaptchaVerify = (data) => {
    setCaptchaVerified(true);
    setError('');
  };
  
  const handleCaptchaError = (errorMsg) => {
    setCaptchaVerified(false);
    setError(errorMsg);
  };
  
  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    analytics.formSubmit('admin_signup');
    analytics.signupSubmit();
    
    // Verify CAPTCHA if present
    if (captchaRef.current && !captchaVerified) {
      const captchaInput = document.querySelector('[data-testid="captcha-answer-input"]');
      if (captchaInput && !captchaInput.value) {
        setError('Please solve the CAPTCHA');
        setLoading(false);
        return;
      }
      const expectedEl = document.querySelector('input[name="captcha_expected"]');
      const answerEl = document.querySelector('input[name="captcha_answer"]');
      if (expectedEl && answerEl) {
        if (parseInt(answerEl.value) !== parseInt(expectedEl.value)) {
          setError('Incorrect CAPTCHA answer. Please try again.');
          setLoading(false);
          return;
        }
      }
    }
    
    // Validate passwords match
    if (adminPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }
    
    // Validate password strength
    if (adminPassword.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }
    
    try {
      // Use company/register endpoint which supports referral codes
      const response = await axios.post(`${API}/company/register`, {
        company_name: companyName,
        admin_email: adminEmail,
        admin_name: adminName,
        admin_password: adminPassword,
        country: country || null,
        referral_code: referralCode || null
      }, {
        withCredentials: true
      });

      // Backend always creates the account on 200. Fire signup_success here —
      // NEVER on button click, NEVER from the login page.
      // register returns email_verification_required (+ role) for every success.
      if (response.data?.email_verification_required || response.data?.role === 'admin') {
        trackSignupSuccess('email');
      } else {
        setError('Signup failed. Please try again.');
        return;
      }

      if (response.data.email_verification_required) {
        setSignupEmail(adminEmail);
        setVerificationPending(true);
      } else {
        setSuccess(true);
        setTimeout(() => {
          navigate('/admin/dashboard');
        }, 2000);
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: signupEmail });
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend');
    } finally {
      setResending(false);
    }
  };
  
  if (verificationPending) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-indigo-50/80 to-purple-100/70 p-4">
        <Card className="w-full max-w-md border-0 shadow-xl shadow-indigo-900/5">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="p-4 bg-blue-100 rounded-full">
                <Mail className="w-12 h-12 text-blue-600" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-testid="verification-pending-title">Check Your Email</h2>
            <p className="text-gray-600 mb-2">
              We've sent a verification link to:
            </p>
            <p className="font-semibold text-gray-900 mb-4" data-testid="verification-email">{signupEmail}</p>
            <p className="text-sm text-gray-500 mb-6">
              Click the link in the email to verify your account and start using Trackly.
            </p>
            <Button
              variant="outline"
              onClick={handleResendVerification}
              disabled={resending}
              className="mb-3"
              data-testid="resend-verification-btn"
            >
              {resending ? 'Sending...' : 'Resend Verification Email'}
            </Button>
            <p className="text-xs text-gray-400">
              Already verified? <Link to="/login" className="text-indigo-600 hover:underline">Login here</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-indigo-50/80 to-purple-100/70 p-4">
        <Card className="w-full max-w-md border-0 shadow-xl shadow-indigo-900/5">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="p-4 bg-indigo-100 rounded-full">
                <CheckCircle className="w-12 h-12 text-indigo-600" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Account Created!</h2>
            <p className="text-gray-600 mb-4">
              Your company admin account has been successfully created. 
              Redirecting to dashboard...
            </p>
            <div className="animate-spin w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full mx-auto"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const platformName = branding?.platform_name || 'Trackly';
  // Default asset is icon-only; custom branding logos often already include the name.
  const isIconOnlyLogo =
    !branding?.logo_url ||
    logoSrc === DEFAULT_LOGO_URL ||
    String(logoSrc).includes('trackly-icon-original');
  
  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-50 via-indigo-50/80 to-purple-100/70 p-4">
      {/* Soft atmosphere */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 -right-16 h-72 w-72 rounded-full bg-indigo-200/40 blur-3xl" />
        <div className="absolute -bottom-28 -left-20 h-80 w-80 rounded-full bg-purple-200/30 blur-3xl" />
      </div>

      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector compact />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Brand — custom logos are full wordmarks; default is icon + name */}
        <div className="mb-6 text-center">
          <div className={`mb-3 flex items-center justify-center ${isIconOnlyLogo ? 'gap-3' : ''}`}>
            <img
              src={logoSrc}
              alt={isIconOnlyLogo ? '' : platformName}
              className={
                isIconOnlyLogo
                  ? 'h-12 w-12 object-contain'
                  : 'h-14 w-auto max-w-[220px] object-contain'
              }
              onError={() => {
                if (logoSrc !== DEFAULT_LOGO_URL) {
                  setLogoSrc(DEFAULT_LOGO_URL);
                }
              }}
            />
            {isIconOnlyLogo && (
              <span className="text-2xl font-bold tracking-tight text-slate-900">
                {platformName}
              </span>
            )}
          </div>
          <p className="text-sm text-slate-600">Create your company account</p>
        </div>
        
        <Card className="border-0 bg-white/90 shadow-xl shadow-indigo-900/5 backdrop-blur-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl text-slate-900">{t('auth.signup')}</CardTitle>
            <CardDescription className="text-slate-500">
              Set up your company and admin access in a few steps
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSignup} onFocus={markFormStart} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="company-name">{t('auth.company_name')}</Label>
                <Input
                  id="company-name"
                  type="text"
                  placeholder="Your Company Ltd."
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                  className="h-11 bg-slate-50/80 border-slate-200 focus-visible:ring-indigo-500"
                  data-testid="company-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="admin-name">{t('auth.admin_name')}</Label>
                <Input
                  id="admin-name"
                  type="text"
                  placeholder="John Doe"
                  value={adminName}
                  onChange={(e) => setAdminName(e.target.value)}
                  required
                  className="h-11 bg-slate-50/80 border-slate-200 focus-visible:ring-indigo-500"
                  data-testid="admin-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="admin-email">{t('auth.email')}</Label>
                <Input
                  id="admin-email"
                  type="email"
                  placeholder="admin@yourcompany.com"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  required
                  className="h-11 bg-slate-50/80 border-slate-200 focus-visible:ring-indigo-500"
                  data-testid="admin-email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="admin-password">{t('auth.password')}</Label>
                <Input
                  id="admin-password"
                  type="password"
                  placeholder="At least 6 characters"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  minLength={6}
                  required
                  className="h-11 bg-slate-50/80 border-slate-200 focus-visible:ring-indigo-500"
                  data-testid="admin-password-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirm-password">{t('auth.confirm_password')}</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  minLength={6}
                  required
                  className="h-11 bg-slate-50/80 border-slate-200 focus-visible:ring-indigo-500"
                  data-testid="confirm-password-input"
                />
              </div>
              
              <div ref={captchaRef}>
                <Captcha 
                  loginType="signup"
                  onVerify={handleCaptchaVerify}
                  onError={handleCaptchaError}
                />
              </div>
              
              {error && (
                <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2.5 text-sm text-red-600" data-testid="error-message">
                  {error}
                </div>
              )}
              
              <Button 
                type="submit" 
                className="h-11 w-full bg-indigo-600 text-base font-medium hover:bg-indigo-700" 
                disabled={loading}
                data-testid="signup-button"
              >
                {loading ? t('common.loading') : t('auth.signup_btn')}
              </Button>
            </form>
            
            <div className="mt-6 text-center text-sm text-slate-600">
              {t('auth.have_account')}{' '}
              <Link 
                to="/admin" 
                className="font-medium text-indigo-600 hover:text-indigo-700"
                data-testid="login-link"
              >
                {t('auth.login')}
              </Link>
            </div>
          </CardContent>
        </Card>
        
        <p className="mt-5 text-center text-xs text-slate-500">
          By creating an account, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
}

export default CompanyAdminSignup;
